import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import BestProductManager from '../components/BestProductManager';
import AdminAnalytics from './AdminAnalytics';
import { T } from '../styles/theme';

function Toggle({ value, onChange, labelOn = 'Si', labelOff = 'No' }) {
  return (
    <button type="button" onClick={() => onChange(!value)}
      className="relative inline-flex h-7 w-12 items-center rounded-full transition-colors"
      style={{ backgroundColor: value ? T.violetDark : T.border }}>
      <span className={`inline-block h-5 w-5 rounded-full bg-white transition-transform ${value ? 'translate-x-6' : 'translate-x-1'}`} />
      <span className="ml-14 text-xs whitespace-nowrap" style={{ color: T.muted }}>{value ? labelOn : labelOff}</span>
    </button>
  );
}

// ========== TAB UTENTI ==========
function TabUtenti({ users, setUsers, stats, setStats, loading }) {
  const [changing, setChanging] = useState(null);
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' }) : '-';
  const ruoloBadge = (ruolo) => {
    if (ruolo === 'admin') return <span className="badge badge-red">ADMIN</span>;
    if (ruolo === 'collaboratore') return <span className="badge badge-amber">COLLABORATORE</span>;
    if (ruolo === 'pro') return <span className="badge badge-amber">PRO</span>;
    return <span className="badge badge-violet">FREE</span>;
  };
  const changeRuolo = async (userId, newRuolo) => {
    setChanging(userId);
    try {
      await api.patch(`/admin/users/${userId}/ruolo`, { ruolo: newRuolo });
      setUsers((p) => p.map((u) => u.id === userId ? { ...u, ruolo: newRuolo } : u));
      // Refresh stats
      api.get('/admin/stats').then((r) => setStats(r.data)).catch(() => {});
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setChanging(null); }
  };
  return (
    <>
      {stats && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
          {[
            { label: 'Totale Utenti', value: stats.totale, gradient: `linear-gradient(135deg, ${T.violetDark}, ${T.violet})` },
            { label: 'Free', value: stats.free, gradient: `linear-gradient(135deg, ${T.violetDeep}, ${T.violet})` },
            { label: 'Pro', value: stats.pro, gradient: `linear-gradient(135deg, #d97706, ${T.amber})` },
          ].map((c) => (
            <div key={c.label} className="card p-5">
              <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{ color: T.muted }}>{c.label}</p>
              <p className="text-4xl font-black" style={{ backgroundImage: c.gradient, WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent' }}>{c.value}</p>
            </div>
          ))}
        </div>
      )}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b" style={{ borderColor: T.border }}><h2 className="section-title" style={{ color: T.violet }}>Utenti</h2></div>
        {loading ? <div className="p-8 text-center" style={{ color: T.muted }}>Caricamento...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr style={{ backgroundColor: T.elem }}>
                {['Username', 'Email', 'Ruolo', 'Registrato', 'Azioni'].map((h) => (
                  <th key={h} className="text-left px-5 py-3 text-[0.72rem] font-semibold uppercase tracking-wider" style={{ color: T.muted }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} className="transition-colors" style={{ borderBottom: `1px solid ${T.border}` }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = T.hover}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        {u.discord_id && u.discord_avatar ? (
                          <img src={`https://cdn.discordapp.com/avatars/${u.discord_id}/${u.discord_avatar}.png`} alt="" className="w-7 h-7 rounded-full" />
                        ) : (
                          <div className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold" style={{ backgroundColor: T.border, color: T.violet }}>{(u.name || '?').charAt(0).toUpperCase()}</div>
                        )}
                        <span className="font-medium" style={{ color: T.text }}>{u.discord_username || u.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3" style={{ color: T.muted }}>{u.email}</td>
                    <td className="px-5 py-3">{ruoloBadge(u.ruolo || 'free')}</td>
                    <td className="px-5 py-3" style={{ color: T.muted }}>{formatDate(u.created_at)}</td>
                    <td className="px-5 py-3">
                      {(u.ruolo || 'free') !== 'admin' && (
                        <select
                          value={u.ruolo || 'free'}
                          onChange={(e) => changeRuolo(u.id, e.target.value)}
                          disabled={changing === u.id}
                          className="input text-xs font-semibold px-3 py-1.5 disabled:opacity-50"
                          style={{ color: T.violet }}
                        >
                          <option value="free">Free</option>
                          <option value="pro">Pro</option>
                          <option value="collaboratore">Collaboratore</option>
                        </select>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ========== TAB GUIDE ==========
function TabGuide() {
  const [guide, setGuide] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ titolo: '', contenuto: '', categoria: 'Vinted', visibile: true });
  const categorie = ['Vinted', 'eBay', 'Mindset', 'Fotografia', 'Prezzi'];

  useEffect(() => { loadGuide(); }, []);
  const loadGuide = () => api.get('/admin/guide').then((r) => setGuide(r.data)).catch(() => {}).finally(() => setLoading(false));

  const resetForm = () => { setForm({ titolo: '', contenuto: '', categoria: 'Vinted', visibile: true }); setEditing(null); };
  const startEdit = (g) => { setForm({ titolo: g.titolo, contenuto: g.contenuto, categoria: g.categoria, visibile: g.visibile }); setEditing(g.id); };

  const handleSave = async () => {
    if (!form.titolo.trim()) return alert('Inserisci un titolo');
    try {
      if (editing) { await api.put(`/admin/guide/${editing}`, form); }
      else { await api.post('/admin/guide', form); }
      resetForm(); loadGuide();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questa guida?')) return;
    await api.delete(`/admin/guide/${id}`); loadGuide();
  };

  return (
    <>
      <div className="card p-5 mb-6">
        <h3 className="text-sm font-bold mb-4" style={{ color: T.violet }}>{editing ? 'Modifica Guida' : 'Nuova Guida'}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label">Titolo</label>
            <input className="input" value={form.titolo} onChange={(e) => setForm({ ...form, titolo: e.target.value })} placeholder="Titolo guida" />
          </div>
          <div>
            <label className="label">Categoria</label>
            <select className="input" value={form.categoria} onChange={(e) => setForm({ ...form, categoria: e.target.value })}>
              {categorie.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="label">Contenuto</label>
          <textarea className="input" style={{ minHeight: 120 }} value={form.contenuto} onChange={(e) => setForm({ ...form, contenuto: e.target.value })} placeholder="Scrivi il contenuto della guida..." />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold" style={{ color: T.muted }}>Visibile</span>
            <Toggle value={form.visibile} onChange={(v) => setForm({ ...form, visibile: v })} />
          </div>
          <div className="flex gap-2">
            {editing && <button onClick={resetForm} className="btn-ghost text-xs">Annulla</button>}
            <button onClick={handleSave} className="btn-primary text-xs">{editing ? 'Aggiorna' : 'Salva'}</button>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b" style={{ borderColor: T.border }}><h2 className="section-title" style={{ color: T.violet }}>Guide ({guide.length})</h2></div>
        {loading ? <div className="p-8 text-center" style={{ color: T.muted }}>Caricamento...</div> : guide.length === 0 ? <div className="p-8 text-center" style={{ color: T.muted }}>Nessuna guida creata</div> : (
          <div className="divide-y" style={{ borderColor: T.border }}>
            {guide.map((g) => (
              <div key={g.id} className="px-5 py-4 flex items-center justify-between transition-colors"
                onMouseEnter={(e) => e.currentTarget.style.backgroundColor = T.hover}
                onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold text-sm" style={{ color: T.text }}>{g.titolo}</span>
                    <span className="badge badge-violet">{g.categoria}</span>
                    {!g.visibile && <span className="badge badge-red">NASCOSTA</span>}
                  </div>
                  <p className="text-xs truncate max-w-lg" style={{ color: T.muted }}>{g.contenuto}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => startEdit(g)} className="btn-ghost text-xs">Modifica</button>
                  <button onClick={() => handleDelete(g.id)} className="btn-danger text-xs">Elimina</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ========== TAB FORNITORI ==========
function TabFornitori() {
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null);
  const [dragIdx, setDragIdx] = useState(null);
  const [dragOverIdx, setDragOverIdx] = useState(null);
  const [form, setForm] = useState({ nome: '', categorie: [], descrizione: '', link_whatsapp: '', telegram_tag: '', telegram_canale: '', telegram_spot: '', paese: 'italiano', attivo: true });
  const tutteCategorie = ['Abbigliamento', 'Scarpe', 'Elettronica', 'Accessori', 'Borse', 'Orologi', 'Profumi', 'Altro'];

  const toggleCategoria = (cat) => {
    setForm((prev) => ({
      ...prev,
      categorie: prev.categorie.includes(cat) ? prev.categorie.filter((c) => c !== cat) : [...prev.categorie, cat],
    }));
  };

  useEffect(() => { loadFornitori(); }, []);
  const loadFornitori = () => api.get('/admin/fornitori').then((r) => setFornitori(r.data)).catch(() => {}).finally(() => setLoading(false));

  const resetForm = () => { setForm({ nome: '', categorie: [], descrizione: '', link_whatsapp: '', telegram_tag: '', telegram_canale: '', telegram_spot: '', paese: 'italiano', attivo: true }); setEditing(null); };
  const startEdit = (f) => {
    const cats = f.categoria ? f.categoria.split(',').map((c) => c.trim()).filter(Boolean) : [];
    setForm({ nome: f.nome, categorie: cats, descrizione: f.descrizione, link_whatsapp: f.link_whatsapp, telegram_tag: f.telegram_tag || '', telegram_canale: f.telegram_canale || '', telegram_spot: f.telegram_spot || '', paese: f.paese || 'italiano', attivo: f.attivo });
    setEditing(f.id);
  };

  const handleSave = async () => {
    if (!form.nome.trim()) return alert('Inserisci un nome');
    if (form.categorie.length === 0) return alert('Seleziona almeno una nicchia');
    const payload = { ...form, categoria: form.categorie.join(',') };
    delete payload.categorie;
    try {
      if (editing) { await api.put(`/admin/fornitori/${editing}`, payload); }
      else { await api.post('/admin/fornitori', payload); }
      resetForm(); loadFornitori();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };
  const handleDelete = async (id) => {
    if (!window.confirm('Eliminare questo fornitore?')) return;
    await api.delete(`/admin/fornitori/${id}`); loadFornitori();
  };

  return (
    <>
      <div className="card p-5 mb-6">
        <h3 className="text-sm font-bold mb-4" style={{ color: T.violet }}>{editing ? 'Modifica Fornitore' : 'Nuovo Fornitore'}</h3>
        <div className="mb-4">
          <label className="label">Nome</label>
          <input className="input" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} placeholder="Nome fornitore" />
        </div>
        <div className="mb-4">
          <label className="label">Nicchie (seleziona una o piu)</label>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 4 }}>
            {tutteCategorie.map((cat) => {
              const selected = form.categorie.includes(cat);
              return (
                <button key={cat} type="button" onClick={() => toggleCategoria(cat)} style={{
                  padding: '5px 12px', borderRadius: 20, fontSize: '0.75rem', fontWeight: 600,
                  fontFamily: T.fontBody, cursor: 'pointer', transition: '0.15s',
                  background: selected ? 'rgba(124,58,237,0.2)' : T.elem,
                  border: `1px solid ${selected ? T.violetDark : T.border}`,
                  color: selected ? T.violet : T.muted,
                }}>
                  {selected ? '✓ ' : ''}{cat}
                </button>
              );
            })}
          </div>
        </div>
        <div className="mb-4">
          <label className="label">Descrizione</label>
          <textarea className="input" style={{ minHeight: 80 }} value={form.descrizione} onChange={(e) => setForm({ ...form, descrizione: e.target.value })} placeholder="Descrizione fornitore..." />
        </div>
        <div className="mb-4">
          <label className="label">Link WhatsApp</label>
          <input className="input" value={form.link_whatsapp} onChange={(e) => setForm({ ...form, link_whatsapp: e.target.value })} placeholder="https://wa.me/393331234567" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
          <div>
            <label className="label">Tag Telegram</label>
            <input className="input" value={form.telegram_tag} onChange={(e) => setForm({ ...form, telegram_tag: e.target.value })} placeholder="@fornitore" />
          </div>
          <div>
            <label className="label">Canale Telegram</label>
            <input className="input" value={form.telegram_canale} onChange={(e) => setForm({ ...form, telegram_canale: e.target.value })} placeholder="https://t.me/canale" />
          </div>
        </div>
        <div className="mb-4">
          <label className="label">Spot Canale Telegram</label>
          <input className="input" value={form.telegram_spot} onChange={(e) => setForm({ ...form, telegram_spot: e.target.value })} placeholder="https://t.me/+AbCdEfG123" />
        </div>
        <div className="mb-4">
          <label className="label">Paese</label>
          <select className="input" value={form.paese} onChange={(e) => setForm({ ...form, paese: e.target.value })}>
            <option value="italiano">Italiano</option>
            <option value="estero">Estero</option>
          </select>
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold" style={{ color: T.muted }}>Attivo</span>
            <Toggle value={form.attivo} onChange={(v) => setForm({ ...form, attivo: v })} />
          </div>
          <div className="flex gap-2">
            {editing && <button onClick={resetForm} className="btn-ghost text-xs">Annulla</button>}
            <button onClick={handleSave} className="btn-primary text-xs">{editing ? 'Aggiorna' : 'Salva'}</button>
          </div>
        </div>
      </div>
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b" style={{ borderColor: T.border }}><h2 className="section-title" style={{ color: T.violet }}>Fornitori ({fornitori.length})</h2></div>
        {loading ? <div className="p-8 text-center" style={{ color: T.muted }}>Caricamento...</div> : fornitori.length === 0 ? <div className="p-8 text-center" style={{ color: T.muted }}>Nessun fornitore</div> : (
          <div style={{ borderColor: T.border }}>
            {fornitori.map((f, idx) => (
              <div key={f.id}
                draggable
                onDragStart={() => setDragIdx(idx)}
                onDragOver={(e) => { e.preventDefault(); setDragOverIdx(idx); }}
                onDragLeave={() => setDragOverIdx(null)}
                onDrop={async () => {
                  if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setDragOverIdx(null); return; }
                  const newList = [...fornitori];
                  const [moved] = newList.splice(dragIdx, 1);
                  newList.splice(idx, 0, moved);
                  setFornitori(newList);
                  setDragIdx(null); setDragOverIdx(null);
                  try { await api.post('/admin/fornitori/reorder', { order: newList.map((x) => x.id) }); } catch {}
                }}
                onDragEnd={() => { setDragIdx(null); setDragOverIdx(null); }}
                className="px-5 py-4 flex items-center justify-between"
                style={{
                  cursor: 'grab', transition: 'background 0.15s, border-color 0.15s',
                  borderBottom: `1px solid ${T.border}`,
                  opacity: dragIdx === idx ? 0.4 : 1,
                  background: dragOverIdx === idx ? 'rgba(124,58,237,0.1)' : 'transparent',
                  borderLeft: dragOverIdx === idx ? `3px solid ${T.violetDark}` : '3px solid transparent',
                }}
                onMouseEnter={(e) => { if (dragIdx === null) e.currentTarget.style.backgroundColor = T.hover; }}
                onMouseLeave={(e) => { if (dragIdx === null) e.currentTarget.style.backgroundColor = 'transparent'; }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: T.muted, cursor: 'grab', flexShrink: 0 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><circle cx="9" cy="6" r="1.5"/><circle cx="15" cy="6" r="1.5"/><circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/><circle cx="9" cy="18" r="1.5"/><circle cx="15" cy="18" r="1.5"/></svg>
                  <span className="text-[10px] font-bold" style={{ color: T.muted, minWidth: 18 }}>{idx + 1}</span>
                </div>
                <div className="flex-1 min-w-0 ml-2">
                  <div className="flex items-center gap-2 mb-1 flex-wrap">
                    <span className="font-semibold text-sm" style={{ color: T.text }}>{f.nome}</span>
                    {(f.categoria || '').split(',').map((c) => c.trim()).filter(Boolean).map((cat) => (
                      <span key={cat} className="badge badge-violet">{cat}</span>
                    ))}
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{
                      background: f.paese === 'estero' ? 'rgba(251,191,36,0.15)' : 'rgba(74,222,128,0.15)',
                      border: `1px solid ${f.paese === 'estero' ? T.amber + '50' : T.green + '50'}`,
                      color: f.paese === 'estero' ? T.amber : T.green,
                    }}>{f.paese === 'estero' ? 'ESTERO' : 'ITALIANO'}</span>
                    {!f.attivo && <span className="badge badge-red">DISATTIVO</span>}
                  </div>
                  <p className="text-xs truncate max-w-md" style={{ color: T.muted }}>{f.descrizione}</p>
                  <div className="flex flex-wrap gap-3 mt-1">
                    {f.link_whatsapp && <span className="text-xs" style={{ color: T.green }}>WhatsApp: {f.link_whatsapp}</span>}
                    {f.telegram_tag && <span className="text-xs" style={{ color: '#38bdf8' }}>Telegram: {f.telegram_tag}</span>}
                    {f.telegram_canale && <span className="text-xs" style={{ color: '#38bdf8' }}>Canale: {f.telegram_canale}</span>}
                    {f.telegram_spot && <span className="text-xs" style={{ color: '#a78bfa' }}>Spot: {f.telegram_spot}</span>}
                  </div>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => startEdit(f)} className="btn-ghost text-xs">Modifica</button>
                  <button onClick={() => handleDelete(f.id)} className="btn-danger text-xs">Elimina</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ========== TAB ABBONAMENTI ==========
function TabAbbonamenti() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => { loadSubs(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const loadSubs = () => api.get('/admin/subscriptions').then(r=>setSubs(r.data)).catch(()=>{}).finally(()=>setLoading(false));

  const changePiano = async (userId, piano) => {
    try { await api.put(`/admin/subscriptions/${userId}`, { piano }); loadSubs(); } catch { alert('Errore'); }
  };

  const filtered = filter === 'all' ? subs : subs.filter(s => s.piano === filter || (filter === 'past_due' && s.stato === 'past_due'));
  const proCount = subs.filter(s=>s.piano==='pro').length;
  const freeCount = subs.filter(s=>s.piano==='free').length;

  return (
    <>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        {[
          {label:'PRO',value:proCount,color:T.violet},
          {label:'FREE',value:freeCount,color:T.muted},
          {label:'Revenue stimata',value:`€${proCount*35}/mese`,color:T.green},
        ].map(c=>(
          <div key={c.label} className="card p-5">
            <p className="text-xs font-semibold uppercase tracking-wider mb-1" style={{color:T.muted}}>{c.label}</p>
            <p className="text-3xl font-black" style={{color:c.color}}>{c.value}</p>
          </div>
        ))}
      </div>
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b flex items-center justify-between" style={{borderColor:T.border}}>
          <h2 className="section-title" style={{color:T.violet}}>Abbonamenti</h2>
          <div style={{display:'flex',gap:4}}>
            {['all','pro','free','past_due'].map(f=>(
              <button key={f} onClick={()=>setFilter(f)} style={{padding:'4px 12px',borderRadius:9999,fontSize:'0.72rem',fontWeight:600,border:`1px solid ${filter===f?T.violet:T.border}`,background:filter===f?'rgba(124,58,237,0.1)':'transparent',color:filter===f?T.violet:T.muted,cursor:'pointer'}}>
                {f==='all'?'Tutti':f==='past_due'?'Past Due':f.toUpperCase()}
              </button>
            ))}
          </div>
        </div>
        {loading ? <div className="p-8 text-center" style={{color:T.muted}}>Caricamento...</div> : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead><tr style={{backgroundColor:T.elem}}>
                {['Username','Piano','Stato','Scadenza','Azioni'].map(h=>(
                  <th key={h} className="text-left px-5 py-3 text-[0.72rem] font-semibold uppercase tracking-wider" style={{color:T.muted}}>{h}</th>
                ))}
              </tr></thead>
              <tbody>{filtered.map(s=>(
                <tr key={s.user_id} style={{borderBottom:`1px solid ${T.border}`}}>
                  <td className="px-5 py-3" style={{color:T.text,fontWeight:500}}>{s.discord_username||s.username}</td>
                  <td className="px-5 py-3"><span className={s.piano==='pro'?'badge badge-violet':'badge badge-red'}>{s.piano?.toUpperCase()}</span></td>
                  <td className="px-5 py-3"><span style={{color:s.stato==='active'?T.green:s.stato==='past_due'?T.amber:T.red,fontWeight:600,fontSize:'0.78rem'}}>{s.stato}</span></td>
                  <td className="px-5 py-3" style={{color:T.muted,fontSize:'0.78rem'}}>{s.scadenza?new Date(s.scadenza).toLocaleDateString('it-IT'):'-'}</td>
                  <td className="px-5 py-3">
                    {s.piano==='pro'?(
                      <button onClick={()=>changePiano(s.user_id,'free')} className="btn-ghost" style={{fontSize:'0.72rem',color:T.red}}>Revoca PRO</button>
                    ):(
                      <button onClick={()=>changePiano(s.user_id,'pro')} className="btn-ghost" style={{fontSize:'0.72rem',color:T.green}}>Concedi PRO</button>
                    )}
                  </td>
                </tr>
              ))}</tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}

// ========== TAB CORSO ==========
function TabCorso() {
  const [moduli, setModuli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingModulo, setEditingModulo] = useState(null);
  const [moduloForm, setModuloForm] = useState({ titolo: '', descrizione: '', ordine: '', piano_richiesto: 'free', attivo: true });
  const [editingLezione, setEditingLezione] = useState(null);
  const [lezioneForm, setLezioneForm] = useState({ modulo_id: '', titolo: '', contenuto_markdown: '', ordine: '', attivo: true });
  const [expandedModulo, setExpandedModulo] = useState(null);
  const [stats, setStats] = useState([]);

  useEffect(() => { loadAll(); }, []);
  const loadAll = () => {
    Promise.all([
      api.get('/admin/corso/moduli').then((r) => setModuli(r.data)),
      api.get('/admin/corso/statistiche').then((r) => setStats(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  };

  const resetModuloForm = () => { setModuloForm({ titolo: '', descrizione: '', ordine: '', piano_richiesto: 'free', attivo: true }); setEditingModulo(null); };
  const resetLezioneForm = () => { setLezioneForm({ modulo_id: '', titolo: '', contenuto_markdown: '', ordine: '', attivo: true }); setEditingLezione(null); };

  const saveModulo = async () => {
    if (!moduloForm.titolo.trim()) return alert('Inserisci un titolo');
    const payload = { ...moduloForm, ordine: parseInt(moduloForm.ordine) || 0 };
    try {
      if (editingModulo) await api.put(`/admin/corso/moduli/${editingModulo}`, payload);
      else await api.post('/admin/corso/moduli', payload);
      resetModuloForm(); loadAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const deleteModulo = async (id) => {
    if (!window.confirm('Eliminare questo modulo e tutte le sue lezioni?')) return;
    await api.delete(`/admin/corso/moduli/${id}`); loadAll();
  };

  const saveLezione = async () => {
    if (!lezioneForm.titolo.trim()) return alert('Inserisci un titolo');
    const payload = { ...lezioneForm, ordine: parseInt(lezioneForm.ordine) || 0 };
    try {
      if (editingLezione) await api.put(`/admin/corso/lezioni/${editingLezione}`, payload);
      else await api.post('/admin/corso/lezioni', payload);
      resetLezioneForm(); loadAll();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
  };

  const deleteLezione = async (id) => {
    if (!window.confirm('Eliminare questa lezione?')) return;
    await api.delete(`/admin/corso/lezioni/${id}`); loadAll();
  };

  const getStatForLezione = (lid) => stats.find((s) => s.lezione_id === lid);

  return (
    <>
      {/* Form modulo */}
      <div className="card p-5 mb-6">
        <h3 className="text-sm font-bold mb-4" style={{ color: T.violet }}>{editingModulo ? 'Modifica Modulo' : 'Nuovo Modulo'}</h3>
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-4 mb-4">
          <div className="sm:col-span-2">
            <label className="label">Titolo</label>
            <input className="input" value={moduloForm.titolo} onChange={(e) => setModuloForm({ ...moduloForm, titolo: e.target.value })} placeholder="Titolo modulo" />
          </div>
          <div>
            <label className="label">Ordine</label>
            <input type="number" className="input" value={moduloForm.ordine} onChange={(e) => setModuloForm({ ...moduloForm, ordine: e.target.value })} placeholder="1" />
          </div>
          <div>
            <label className="label">Piano</label>
            <select className="input" value={moduloForm.piano_richiesto} onChange={(e) => setModuloForm({ ...moduloForm, piano_richiesto: e.target.value })}>
              <option value="free">Free</option>
              <option value="pro">Pro</option>
            </select>
          </div>
        </div>
        <div className="mb-4">
          <label className="label">Descrizione</label>
          <input className="input" value={moduloForm.descrizione} onChange={(e) => setModuloForm({ ...moduloForm, descrizione: e.target.value })} placeholder="Descrizione breve" />
        </div>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="text-xs font-semibold" style={{ color: T.muted }}>Attivo</span>
            <Toggle value={moduloForm.attivo} onChange={(v) => setModuloForm({ ...moduloForm, attivo: v })} />
          </div>
          <div className="flex gap-2">
            {editingModulo && <button onClick={resetModuloForm} className="btn-ghost text-xs">Annulla</button>}
            <button onClick={saveModulo} className="btn-primary text-xs">{editingModulo ? 'Aggiorna' : 'Crea Modulo'}</button>
          </div>
        </div>
      </div>

      {/* Form lezione (visible when adding/editing) */}
      {(editingLezione || lezioneForm.modulo_id) && (
        <div className="card p-5 mb-6">
          <h3 className="text-sm font-bold mb-4" style={{ color: T.violet }}>{editingLezione ? 'Modifica Lezione' : 'Nuova Lezione'}</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
            <div className="sm:col-span-2">
              <label className="label">Titolo</label>
              <input className="input" value={lezioneForm.titolo} onChange={(e) => setLezioneForm({ ...lezioneForm, titolo: e.target.value })} placeholder="Titolo lezione" />
            </div>
            <div>
              <label className="label">Ordine</label>
              <input type="number" className="input" value={lezioneForm.ordine} onChange={(e) => setLezioneForm({ ...lezioneForm, ordine: e.target.value })} placeholder="1" />
            </div>
          </div>
          <div className="mb-4">
            <label className="label">Contenuto Markdown</label>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              <textarea className="input" style={{ minHeight: 300, fontFamily: T.fontMono, fontSize: 13 }} value={lezioneForm.contenuto_markdown}
                onChange={(e) => setLezioneForm({ ...lezioneForm, contenuto_markdown: e.target.value })} placeholder="# Titolo&#10;&#10;Scrivi il contenuto in markdown..." />
              <div className="rounded-lg p-4 overflow-y-auto text-sm" style={{ backgroundColor: T.bg, border: `1px solid ${T.border}`, minHeight: 300, maxHeight: 400 }}>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-2" style={{ color: T.muted }}>Preview</div>
                <div style={{ color: '#c4b5fd' }}>
                  {lezioneForm.contenuto_markdown ? lezioneForm.contenuto_markdown.split('\n').map((line, i) => {
                    if (line.startsWith('# ')) return <h1 key={i} className="text-xl font-bold mt-4 mb-2" style={{ color: T.text }}>{line.slice(2)}</h1>;
                    if (line.startsWith('## ')) return <h2 key={i} className="text-lg font-bold mt-3 mb-1" style={{ color: T.text }}>{line.slice(3)}</h2>;
                    if (line.startsWith('- ')) return <li key={i} className="ml-4 list-disc">{line.slice(2)}</li>;
                    if (line.trim() === '') return <div key={i} className="h-2" />;
                    return <p key={i} className="mb-1">{line}</p>;
                  }) : <span style={{ color: T.muted }}>Anteprima markdown...</span>}
                </div>
              </div>
            </div>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-xs font-semibold" style={{ color: T.muted }}>Attiva</span>
              <Toggle value={lezioneForm.attivo} onChange={(v) => setLezioneForm({ ...lezioneForm, attivo: v })} />
            </div>
            <div className="flex gap-2">
              <button onClick={resetLezioneForm} className="btn-ghost text-xs">Annulla</button>
              <button onClick={saveLezione} className="btn-primary text-xs">{editingLezione ? 'Aggiorna' : 'Crea Lezione'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Lista moduli */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b" style={{ borderColor: T.border }}>
          <h2 className="section-title" style={{ color: T.violet }}>Moduli e Lezioni ({moduli.length})</h2>
        </div>
        {loading ? <div className="p-8 text-center" style={{ color: T.muted }}>Caricamento...</div> : moduli.length === 0 ? <div className="p-8 text-center" style={{ color: T.muted }}>Nessun modulo</div> : (
          <div>
            {moduli.map((m) => (
              <div key={m.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                <div className="px-5 py-4 flex items-center justify-between transition-colors"
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = T.hover}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}>
                  <div className="flex items-center gap-3 flex-1">
                    <button onClick={() => setExpandedModulo(expandedModulo === m.id ? null : m.id)} className="text-sm" style={{ color: T.muted }}>
                      {expandedModulo === m.id ? '▼' : '▶'}
                    </button>
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-sm" style={{ color: T.text }}>#{m.ordine} {m.titolo}</span>
                        <span className={`badge ${m.piano_richiesto === 'pro' ? 'badge-amber' : 'badge-violet'}`}>{m.piano_richiesto.toUpperCase()}</span>
                        {!m.attivo && <span className="badge badge-red">DISATTIVO</span>}
                      </div>
                      <p className="text-xs mt-0.5" style={{ color: T.muted }}>{m.lezioni.length} lezioni — {m.descrizione}</p>
                    </div>
                  </div>
                  <div className="flex gap-2">
                    <button onClick={() => { setLezioneForm({ modulo_id: m.id, titolo: '', contenuto_markdown: '', ordine: String(m.lezioni.length + 1), attivo: true }); setEditingLezione(null); }}
                      className="btn-ghost text-xs" style={{ color: T.green, borderColor: `${T.green}44` }}>+ Lezione</button>
                    <button onClick={() => { setModuloForm({ titolo: m.titolo, descrizione: m.descrizione, ordine: String(m.ordine), piano_richiesto: m.piano_richiesto, attivo: m.attivo }); setEditingModulo(m.id); }}
                      className="btn-ghost text-xs">Modifica</button>
                    <button onClick={() => deleteModulo(m.id)} className="btn-danger text-xs">Elimina</button>
                  </div>
                </div>
                {expandedModulo === m.id && m.lezioni.map((l) => {
                  const st = getStatForLezione(l.id);
                  return (
                    <div key={l.id} className="pl-14 pr-5 py-3 flex items-center justify-between" style={{ borderTop: `1px solid ${T.border}`, backgroundColor: T.elem }}>
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className="text-xs" style={{ color: T.muted }}>#{l.ordine}</span>
                          <span className="text-sm" style={{ color: T.text }}>{l.titolo}</span>
                          {!l.attivo && <span className="badge badge-red text-[10px]">OFF</span>}
                        </div>
                        {st && <span className="text-[10px]" style={{ color: T.muted }}>{st.completamenti}/{st.totale_utenti} utenti completato</span>}
                      </div>
                      <div className="flex gap-2">
                        <button onClick={() => { setLezioneForm({ modulo_id: l.modulo_id || m.id, titolo: l.titolo, contenuto_markdown: l.contenuto_markdown, ordine: String(l.ordine), attivo: l.attivo }); setEditingLezione(l.id); }}
                          className="btn-ghost text-[11px]">Modifica</button>
                        <button onClick={() => deleteLezione(l.id)} className="btn-danger text-[11px]">Elimina</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

// ========== ADMIN MAIN ==========
// ========== TAB TICKETS ==========
function TabTickets() {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTicket, setActiveTicket] = useState(null);
  const [chatMessages, setChatMessages] = useState([]);
  const [replyText, setReplyText] = useState('');
  const [replyStato, setReplyStato] = useState('');

  useEffect(() => { loadTickets(); }, []);
  const loadTickets = () => api.get('/tickets').then(r => setTickets(r.data)).catch(() => {}).finally(() => setLoading(false));

  const openChat = async (ticket) => {
    setActiveTicket(ticket);
    setReplyStato(ticket.stato);
    try {
      const res = await api.get(`/tickets/${ticket.id}/messages`);
      setChatMessages(res.data);
    } catch { setChatMessages([]); }
  };

  const sendReply = async () => {
    if (!replyText.trim() || !activeTicket) return;
    try {
      await api.post(`/tickets/${activeTicket.id}/messages`, { ticket_id: activeTicket.id, content: replyText.trim() });
      setReplyText('');
      const res = await api.get(`/tickets/${activeTicket.id}/messages`);
      setChatMessages(res.data);
      loadTickets();
    } catch { alert('Errore'); }
  };

  const changeStato = async (stato) => {
    if (!activeTicket) return;
    try {
      await api.put(`/tickets/${activeTicket.id}`, { stato, risposta: '' });
      setActiveTicket(prev => ({ ...prev, stato }));
      setReplyStato(stato);
      loadTickets();
    } catch { alert('Errore'); }
  };

  const statoBadge = (stato) => {
    if (stato === 'risolto') return <span className="badge badge-green">Risolto</span>;
    if (stato === 'in_lavorazione') return <span className="badge badge-amber">In lavorazione</span>;
    return <span className="badge badge-red">Aperto</span>;
  };
  const prioritaBadge = (p) => {
    if (p === 'alta') return <span style={{color:T.red,fontWeight:700,fontSize:'0.72rem'}}>Alta</span>;
    if (p === 'bassa') return <span style={{color:T.green,fontWeight:700,fontSize:'0.72rem'}}>Bassa</span>;
    return <span style={{color:T.amber,fontWeight:700,fontSize:'0.72rem'}}>Normale</span>;
  };
  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '-';

  return (
    <div style={{ display: 'grid', gridTemplateColumns: activeTicket ? '1fr 1fr' : '1fr', gap: 20 }}>
      {/* LISTA TICKET */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b" style={{borderColor:T.border}}>
          <h2 className="section-title" style={{color:T.violet}}>Ticket Supporto</h2>
          <p style={{fontSize:'0.78rem',color:T.muted,marginTop:4}}>{tickets.filter(t=>t.stato==='aperto').length} aperti, {tickets.filter(t=>t.stato==='in_lavorazione').length} in lavorazione, {tickets.length} totali</p>
        </div>
        {loading ? <div className="p-8 text-center" style={{color:T.muted}}>Caricamento...</div> : tickets.length === 0 ? (
          <div className="p-8 text-center" style={{color:T.muted}}>Nessun ticket</div>
        ) : (
          <div style={{maxHeight:'60vh',overflowY:'auto'}}>
            {tickets.map(t => (
              <div key={t.id} onClick={() => openChat(t)}
                style={{padding:'12px 20px',borderBottom:`1px solid ${T.border}`,cursor:'pointer',transition:'background 0.15s',background:activeTicket?.id===t.id?'rgba(124,58,237,0.08)':'transparent'}}
                onMouseEnter={e=>{if(activeTicket?.id!==t.id)e.currentTarget.style.background=T.elem;}} onMouseLeave={e=>{if(activeTicket?.id!==t.id)e.currentTarget.style.background='transparent';}}>
                <div style={{display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <div>
                    <span style={{fontWeight:600,fontSize:'0.85rem',color:T.text}}>{t.titolo}</span>
                    <div style={{fontSize:'0.72rem',color:T.muted,marginTop:2}}>#{t.id} • {t.username} • {formatDate(t.created_at)}</div>
                  </div>
                  <div style={{textAlign:'right'}}>
                    {statoBadge(t.stato)}
                    <div style={{marginTop:4}}>{prioritaBadge(t.priorita)}</div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* CHAT TICKET */}
      {activeTicket && (
        <div className="card overflow-hidden" style={{display:'flex',flexDirection:'column',maxHeight:'70vh'}}>
          <div className="px-5 py-4 border-b" style={{borderColor:T.border,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
            <div>
              <h3 style={{fontFamily:'Syne,sans-serif',fontWeight:700,fontSize:'0.95rem',color:T.text}}>{activeTicket.titolo}</h3>
              <p style={{fontSize:'0.72rem',color:T.muted}}>#{activeTicket.id} • {activeTicket.username} • {formatDate(activeTicket.created_at)}</p>
            </div>
            <select value={replyStato} onChange={e=>changeStato(e.target.value)} className="input" style={{width:'auto',fontSize:'0.78rem',padding:'6px 12px'}}>
              <option value="aperto">Aperto</option>
              <option value="in_lavorazione">In lavorazione</option>
              <option value="risolto">Risolto</option>
            </select>
          </div>

          {/* Descrizione */}
          <div style={{padding:'12px 20px',background:T.elem,borderBottom:`1px solid ${T.border}`,fontSize:'0.8rem',color:T.muted}}>
            <span style={{fontWeight:600,color:T.violet,fontSize:'0.68rem',textTransform:'uppercase'}}>Descrizione: </span>
            {activeTicket.descrizione}
          </div>

          {/* Messaggi */}
          <div style={{flex:1,overflowY:'auto',padding:16,display:'flex',flexDirection:'column',gap:10}}>
            {chatMessages.length === 0 && <div style={{textAlign:'center',color:T.muted,fontSize:'0.82rem',padding:20}}>Nessun messaggio. Rispondi qui sotto.</div>}
            {chatMessages.map((m,i) => (
              <div key={i} style={{display:'flex',justifyContent:m.sender_role==='admin'?'flex-end':'flex-start',gap:8,alignItems:'flex-end'}}>
                {m.sender_role==='user' && <div style={{width:28,height:28,borderRadius:'50%',background:T.border,display:'flex',alignItems:'center',justifyContent:'center',fontSize:'0.7rem',color:T.violet,fontWeight:700,flexShrink:0}}>{(activeTicket.username||'U').charAt(0).toUpperCase()}</div>}
                <div style={{
                  maxWidth:'75%',padding:'10px 14px',
                  borderRadius:m.sender_role==='admin'?'16px 16px 4px 16px':'16px 16px 16px 4px',
                  background:m.sender_role==='admin'?'linear-gradient(135deg,#7c3aed,#4f46e5)':T.elem,
                  border:m.sender_role==='admin'?'none':`1px solid ${T.border}`,
                  color:T.text,fontSize:'0.82rem',lineHeight:1.5,whiteSpace:'pre-wrap'
                }}>
                  <div style={{fontSize:'0.65rem',color:m.sender_role==='admin'?'rgba(255,255,255,0.7)':T.muted,marginBottom:3,fontWeight:600}}>
                    {m.sender_role==='admin'?'Tu (Staff)':activeTicket.username} • {new Date(m.created_at).toLocaleTimeString('it-IT',{hour:'2-digit',minute:'2-digit'})}
                  </div>
                  {m.content}
                </div>
              </div>
            ))}
          </div>

          {/* Input risposta */}
          <div style={{padding:'12px 16px',borderTop:`1px solid ${T.border}`,display:'flex',gap:8}}>
            <input value={replyText} onChange={e=>setReplyText(e.target.value)} onKeyDown={e=>{if(e.key==='Enter'){e.preventDefault();sendReply();}}}
              placeholder="Scrivi una risposta..." className="input" style={{flex:1,borderRadius:9999,padding:'10px 16px'}}/>
            <button onClick={sendReply} disabled={!replyText.trim()} className="btn-primary" style={{borderRadius:9999,padding:'10px 18px',opacity:!replyText.trim()?0.5:1}}>
              Invia
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// ========== TAB LANCIO ==========
function TabLancio() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);

  const loadStatus = () => api.get('/admin/system-status').then(r => setStatus(r.data)).catch(() => setStatus(null)).finally(() => setLoading(false));

  useEffect(() => {
    loadStatus();
    const interval = setInterval(loadStatus, 30000);
    return () => clearInterval(interval);
  }, []);

  const forzaBackup = async () => {
    setActionLoading('backup');
    try {
      const res = await api.post('/admin/backup');
      alert(`Backup completato: ${res.data.file} (${res.data.dimensione})`);
      loadStatus();
    } catch (e) { alert(e.response?.data?.detail || 'Errore backup'); }
    finally { setActionLoading(null); }
  };

  const pulisciCache = async () => {
    setActionLoading('cache');
    try {
      const res = await api.post('/admin/clear-cache');
      alert(`Cache Discord svuotata (${res.data.cache_svuotata} voci rimosse)`);
      loadStatus();
    } catch (e) { alert(e.response?.data?.detail || 'Errore'); }
    finally { setActionLoading(null); }
  };

  const exportCSV = () => {
    window.open(`${api.defaults.baseURL}/admin/export-users-csv?token=${localStorage.getItem('token')}`, '_blank');
  };

  const formatDate = (d) => d ? new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '-';

  if (loading) return <div className="p-8 text-center" style={{ color: T.muted }}>Caricamento stato sistema...</div>;

  return (
    <>
      {/* SEZIONE 1 — Stato sistema */}
      <div className="mb-8">
        <h3 className="text-sm font-bold mb-4 flex items-center gap-2" style={{ color: T.violet }}>
          Stato Sistema
          <span className="text-[10px] font-normal" style={{ color: T.muted }}>aggiornamento ogni 30s</span>
        </h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
          {[
            { label: 'Backend', value: status?.backend === 'online' ? 'ONLINE' : 'OFFLINE', color: status?.backend === 'online' ? T.green : T.red },
            { label: 'Utenti totali', value: status?.totale_utenti || 0, color: T.violet },
            { label: 'Iscrizioni oggi', value: status?.iscrizioni_oggi || 0, color: T.green },
            { label: 'Login 24h', value: status?.login_24h || 0, color: T.amber },
            { label: 'Abbonamenti PRO', value: status?.abbonamenti_attivi || 0, color: T.violet },
            { label: 'Ultimo backup', value: status?.ultimo_backup?.data ? formatDate(status.ultimo_backup.data) : 'Mai', color: T.muted },
          ].map(c => (
            <div key={c.label} className="card p-4">
              <p className="text-[10px] font-semibold uppercase tracking-wider mb-1" style={{ color: T.muted }}>{c.label}</p>
              <p className="text-xl font-black" style={{ color: c.color }}>{c.value}</p>
              {c.label === 'Ultimo backup' && status?.ultimo_backup?.dimensione && (
                <p className="text-[10px] mt-0.5" style={{ color: T.muted }}>{status.ultimo_backup.dimensione}</p>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* SEZIONE 2 — Azioni rapide */}
      <div className="mb-8">
        <h3 className="text-sm font-bold mb-4" style={{ color: T.violet }}>Azioni Rapide Pre-Lancio</h3>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <button onClick={forzaBackup} disabled={actionLoading === 'backup'} className="card p-5 text-left transition-all hover:scale-[1.02]"
            style={{ border: `1px solid ${T.border}`, cursor: 'pointer', opacity: actionLoading === 'backup' ? 0.6 : 1 }}>
            <div className="text-2xl mb-2">🔄</div>
            <div className="font-semibold text-sm" style={{ color: T.text }}>Forza Backup DB</div>
            <p className="text-xs mt-1" style={{ color: T.muted }}>{actionLoading === 'backup' ? 'Backup in corso...' : 'Esegui backup manuale del database'}</p>
          </button>
          <button onClick={pulisciCache} disabled={actionLoading === 'cache'} className="card p-5 text-left transition-all hover:scale-[1.02]"
            style={{ border: `1px solid ${T.border}`, cursor: 'pointer', opacity: actionLoading === 'cache' ? 0.6 : 1 }}>
            <div className="text-2xl mb-2">🧹</div>
            <div className="font-semibold text-sm" style={{ color: T.text }}>Pulisci Cache Discord</div>
            <p className="text-xs mt-1" style={{ color: T.muted }}>{actionLoading === 'cache' ? 'Pulizia...' : `${status?.cache_discord_size || 0} voci in cache`}</p>
          </button>
          <button onClick={exportCSV} className="card p-5 text-left transition-all hover:scale-[1.02]"
            style={{ border: `1px solid ${T.border}`, cursor: 'pointer' }}>
            <div className="text-2xl mb-2">📊</div>
            <div className="font-semibold text-sm" style={{ color: T.text }}>Export Utenti CSV</div>
            <p className="text-xs mt-1" style={{ color: T.muted }}>Scarica CSV con tutti gli utenti</p>
          </button>
        </div>
      </div>

      {/* SEZIONE 3 — Log iscrizioni */}
      <div className="card overflow-hidden">
        <div className="px-5 py-4 border-b" style={{ borderColor: T.border }}>
          <h2 className="section-title" style={{ color: T.violet }}>Ultime 20 Iscrizioni</h2>
          <p className="text-xs mt-1" style={{ color: T.muted }}>Aggiornamento automatico ogni 30 secondi</p>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr style={{ backgroundColor: T.elem }}>
              {['Username', 'Piano', 'Data Registrazione', 'Discord ID'].map(h => (
                <th key={h} className="text-left px-5 py-3 text-[0.72rem] font-semibold uppercase tracking-wider" style={{ color: T.muted }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {(status?.ultimi_utenti || []).map(u => (
                <tr key={u.id} style={{ borderBottom: `1px solid ${T.border}` }}>
                  <td className="px-5 py-3">
                    <span className="font-medium" style={{ color: T.text }}>{u.discord_username || u.username}</span>
                  </td>
                  <td className="px-5 py-3">
                    <span className={u.piano === 'pro' ? 'badge badge-violet' : u.piano === 'admin' ? 'badge badge-red' : 'badge badge-amber'}>
                      {(u.piano || 'free').toUpperCase()}
                    </span>
                  </td>
                  <td className="px-5 py-3" style={{ color: T.muted, fontSize: '0.78rem' }}>{formatDate(u.registrato)}</td>
                  <td className="px-5 py-3" style={{ color: T.muted, fontSize: '0.72rem', fontFamily: T.fontMono }}>{u.discord_id || '-'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
}

const TABS_ADMIN = [
  { key: 'utenti', label: 'Utenti' },
  { key: 'tickets', label: '🎫 Ticket' },
  { key: 'fornitori', label: 'Fornitori' },
  { key: 'best-product', label: 'Best Product' },
  { key: 'corso', label: 'Corso' },
  { key: 'abbonamenti', label: '💳 Abbonamenti' },
  { key: 'analytics', label: '📊 Analytics' },
  { key: 'lancio', label: '🚀 Lancio' },
];

const TABS_COLLAB = [
  { key: 'fornitori', label: 'Fornitori' },
  { key: 'best-product', label: 'Best Product' },
  { key: 'tickets', label: '🎫 Ticket' },
];

export default function Admin() {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(null);
  const [userRuolo, setUserRuolo] = useState(null);

  useEffect(() => {
    api.get('/auth/me').then((r) => {
      const ruolo = r.data.ruolo;
      setUserRuolo(ruolo);
      if (ruolo !== 'admin' && !r.data.is_admin && ruolo !== 'collaboratore') navigate('/dashboard');
      // Tab di default: collaboratori vedono fornitori, admin vedono utenti
      setActiveTab(ruolo === 'collaboratore' ? 'fornitori' : 'utenti');
    }).catch(() => navigate('/login'));

    Promise.all([
      api.get('/admin/users').then((r) => setUsers(r.data)),
      api.get('/admin/stats').then((r) => setStats(r.data)),
    ]).catch(() => {}).finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen" style={{ backgroundColor: T.bg }}>
      {/* Header */}
      <div className="border-b" style={{ borderColor: T.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 flex items-center justify-between">
          <div>
            <h1 className="page-title" style={{ color: T.violet }}>Pannello Admin — Impero Elite</h1>
            <p className="text-sm mt-1" style={{ color: T.muted }}>Gestione completa della community</p>
          </div>
          <button onClick={() => navigate('/dashboard')} className="btn-ghost">
            Torna alla Dashboard
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="border-b" style={{ borderColor: T.border }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex gap-1 overflow-x-auto">
          {(userRuolo === 'collaboratore' ? TABS_COLLAB : TABS_ADMIN).map((t) => (
            <button key={t.key} onClick={() => setActiveTab(t.key)}
              className="px-5 py-3 text-sm font-semibold transition-colors whitespace-nowrap relative"
              style={{ color: activeTab === t.key ? T.violet : T.muted }}>
              {t.label}
              {activeTab === t.key && <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-t" style={{ backgroundColor: T.violet }} />}
            </button>
          ))}
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {activeTab === 'utenti' && <TabUtenti users={users} setUsers={setUsers} stats={stats} setStats={setStats} loading={loading} />}
        {activeTab === 'tickets' && <TabTickets />}
        {activeTab === 'fornitori' && <TabFornitori />}
        {activeTab === 'best-product' && <BestProductManager />}
        {activeTab === 'corso' && <TabCorso />}
        {activeTab === 'abbonamenti' && <TabAbbonamenti />}
        {activeTab === 'analytics' && <AdminAnalytics />}
        {activeTab === 'lancio' && <TabLancio />}
      </main>
    </div>
  );
}
